import { useContext, useEffect, useState } from "react";
import { IPackage } from "dto/IPackage";
import { IAttachment } from "dto/IAttachment";
import KeysContext from "contexts/KeysContext";
import usePDF from "hooks/usePDF";
import PDFViewer from "components/PDFViewer";
import EmptyPanel from "components/EmptyPanel";
import styles from "./styles.module.scss";

interface Props {
  /** Wybrana pozycja „Akceptacja i Podpis" (z plikiem jako Attachments[0]). */
  pkg: IPackage;
}

/**
 * Panel prawy dla „Akceptacja i Podpis / Otrzymane" — podgląd pliku wybranej
 * pozycji. Klucze obiektu pliku (pkg.ObjectKeys) ładujemy do KeysContext, a plik
 * odszyfrowujemy generyczną ścieżką (usePDF.decryptPackage) i pokazujemy w
 * PDFViewer. Deszyfrowanie zadziała, gdy BE dostarczy klucz zawinięty dla
 * bieżącego (odbierającego) członka.
 */
const AcceptanceFilePreview = ({ pkg }: Props) => {
  const attachment = pkg.Attachments?.[0] as IAttachment | undefined;
  const { decryptPackage } = usePDF();
  const { keys, setKeys } = useContext(KeysContext);

  const [blob, setBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Załaduj klucze obiektu pozycji do KeysContext (inaczej deszyfrowanie = nokey).
  useEffect(() => {
    const objectKeys = pkg.ObjectKeys ?? [];
    if (objectKeys.length === 0) return;
    const missing = objectKeys.filter((k) => !keys.some((x) => x.Id === k.Id));
    if (missing.length > 0) setKeys([...keys, ...missing]);
  }, [pkg.ObjectKeys, keys, setKeys]);

  const keyReady =
    !!attachment?.EncryptionKeyId &&
    keys.some((k) => k.Id === attachment.EncryptionKeyId);

  // Odszyfruj plik dopiero gdy jego klucz jest już w kontekście.
  useEffect(() => {
    let cancelled = false;
    setBlob(null);
    setError(null);
    if (!attachment?.Uri || !attachment?.EncryptionKeyId || !keyReady) return;
    setLoading(true);
    (async () => {
      try {
        const b = await decryptPackage(attachment, attachment.Kind, false);
        if (!cancelled) setBlob(b);
      } catch (e) {
        if (!cancelled)
          setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachment?.Uri, attachment?.EncryptionKeyId, keyReady]);

  if (!attachment?.Uri) {
    return (
      <div className={styles.message}>
        Ta pozycja nie ma pliku do podglądu.
      </div>
    );
  }
  if (loading) {
    return <div className={styles.message}>Ładowanie podglądu…</div>;
  }
  if (error) {
    return (
      <div className={styles.error}>
        Nie udało się odszyfrować pliku tej pozycji. Klucz obiektu musi być
        zawinięty dla bieżącego (odbierającego) członka. Błąd: {error}
      </div>
    );
  }
  if (blob) {
    return (
      <PDFViewer file={blob} title={attachment.FileName} showSignButton />
    );
  }
  return <EmptyPanel />;
};

export default AcceptanceFilePreview;
